import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminEnvelope } from "./bonusAdminBody";
import { getSeriesQueryKey } from "./useGetSeries";
import { LIST_SERIES_QUERY_KEY } from "./useListSeries";

type StoredToken = { AccessToken?: string };

// Unlike campaigns, series never expire (`ends_at` is forced null), so there
// is no `expire` transition — only activate/pause.
export type SeriesTransitionAction = "activate" | "pause";

const ROUTE_MAP: Record<SeriesTransitionAction, string> = {
  activate: API_ROUTES.ACTIVATE_DYNAMIC_BONUS_SERIES,
  pause:    API_ROUTES.PAUSE_DYNAMIC_BONUS_SERIES,
};

async function postTransition(
  seriesId: string,
  action: SeriesTransitionAction,
): Promise<string | undefined> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = ROUTE_MAP[action].replace("{series_id}", seriesId);
  const response = await instance.post(url);
  return bonusAdminEnvelope(response).message;
}

export const useSeriesTransition = (seriesId: string) => {
  const queryClient = useQueryClient();

  return useMutation<string | undefined, Error, SeriesTransitionAction>({
    mutationFn: (action) => postTransition(seriesId, action),
    onSuccess: (beMessage) => {
      queryClient.invalidateQueries({ queryKey: LIST_SERIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: getSeriesQueryKey(seriesId) });
      message.success(beMessage || AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED);
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    },
  });
};
